import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@staff-app/ui";
import { ArrowLeft, Trash2 } from "lucide-react";
import { useState } from "react";
import { useAuthStore } from "@/stores/auth-store";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { DocumentUploadDialog } from "../components/DocumentUploadDialog";
import { useCustomerDocuments } from "../hooks/useCustomerDocuments";
import {
  useStaffGetCustomer,
  useStaffGetCustomerInfo,
} from "../hooks/useStaffCustomer";
import { parseStripeId } from "../lib/customer-info";
import { CopyButton } from "@/components/CopyButton";
import { formatDayCentral } from "@/lib/format-date";
import {
  CustomerDocument,
  DOC_TYPE_LABEL,
  DOC_TYPES,
  DocType,
  documentsApi,
} from "../lib/documents-api";
import { InvoiceLinkCell } from "@/features/documents/components/InvoiceLinkCell";


/**
 * The upload dialog defaults Title to the filename minus its extension, so for most
 * documents the two are the same string and showing both just prints it twice. Show the
 * filename only when it genuinely differs — i.e. when someone gave the document a real
 * title ("Acme MSA 2026" for scan_001.pdf), which is exactly when it is worth seeing.
 */
const filenameAddsInfo = (title: string, filename: string | null): boolean => {
  if (!filename) return false;
  const stem = filename.replace(/\.[^.]+$/, "");
  return stem.trim().toLowerCase() !== title.trim().toLowerCase();
};

export function CustomerDocumentsPage() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  // Owner is a nicety, not a gate — the page works before this resolves, and Apollo has it
  // cached already if you arrived from the customer card.
  const { data: info } = useStaffGetCustomer(accountId);
  const ownerEmail = info?.staffGetCustomer?.email;
  // Stripe id lives in the company's payment blob, same source the customer card reads.
  // Optional: a customer with no Stripe record is normal, so the row is simply omitted.
  const { data: infoBlob } = useStaffGetCustomerInfo(accountId ?? null);
  const stripeId = parseStripeId(infoBlob?.staffGetCustomerInfo);
  const [q, setQ] = useState("");
  const [docType, setDocType] = useState<DocType | undefined>(undefined);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // ROOT-only, matching the endpoints: archive/restore and DELETE are all @RequireRoles(ROOT).
  // Hidden rather than disabled for support — a greyed button they can never use is noise.
  const isRoot = !!useAuthStore((s) => s.adminUser)?.roles?.includes("root");

  // Archived rows are visible to ROOT and hidden from everyone else. This used to be hard-off
  // with a comment saying nothing in the UI archives a document — no longer true, and it
  // matters: deleting is a two-step (archive, then permanently delete), so hiding archived rows
  // would make the second step unreachable through the UI.
  const { documents, total, fuzzy, loading, error, reload } =
    useCustomerDocuments(accountId, {
      q: q.trim() || undefined,
      docType,
      includeArchived: isRoot,
    });

  /**
   * Open a document. The presigned URL is minted per click and lives ~5 minutes; it is
   * deliberately never stored in component state or a link href, so it cannot be copied
   * out of the DOM or survive a re-render.
   */
  const open = async (
    doc: CustomerDocument,
    disposition: "inline" | "attachment",
  ) => {
    setBusyId(doc.id);
    try {
      const { url } = await documentsApi.download(doc.id, disposition);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: unknown) {
      toast.error("Could not open document", {
        description: (e as Error)?.message ?? "Unknown error",
      });
    } finally {
      setBusyId(null);
    }
  };

  // NOTE (2026-08-12): there is deliberately no verify/unverify action here. Uploads
  // self-stamp `verified_at` with the uploader (documents.service.ts `upload`), so a manual
  // toggle was a button that only ever undid the thing the upload had just done. The
  // Verification column still SHOWS the state, because Apply Coupon refuses an unverified
  // tax-exemption certificate and an operator needs to see which rows qualify.
  //
  // Consequence to know about: a red row now has no UI path back to green. That is fine for
  // anything uploaded from here, but a document filed before self-stamping landed stays red.
  // `POST /documents/:id/verify` and `documentsApi.verify()` both still exist and are still
  // ROOT+SUPPORT, so a real verification process (a different actor signing off, which is
  // what makes the gate mean something) can be built on them without backend work.

  /**
   * Step 1 of deleting: archive. Reversible, bytes untouched, and it is what proves intent
   * before the irreversible step is offered at all.
   */
  const doArchive = async (doc: CustomerDocument) => {
    setBusyId(doc.id);
    try {
      await documentsApi.archive(doc.id, "deleted from the Documents tab");
      toast.success("Document archived", {
        description: `${doc.title} is archived. Delete it again to erase it permanently.`,
      });
      reload();
    } catch (e: unknown) {
      toast.error("Could not archive document", {
        description: (e as Error)?.message ?? "Unknown error",
      });
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Step 2: permanent deletion. The server re-checks everything that matters (archived first,
   * no legal hold, not backing a live discount) — this is not the gate, it is the request.
   */
  const doPurge = async (doc: CustomerDocument) => {
    setBusyId(doc.id);
    try {
      const { purged } = await documentsApi.purge(doc.id);
      toast.success("Document deleted permanently", {
        description: `${doc.title} — ${purged.objectsDeleted} object version(s) erased from storage. This cannot be undone.`,
      });
      reload();
    } catch (e: unknown) {
      // The server's refusals are written to be read by an operator (why, and what to do
      // next), so surface them verbatim rather than a generic failure.
      toast.error("Could not delete document", {
        description: (e as Error)?.message ?? "Unknown error",
      });
    } finally {
      setBusyId(null);
    }
  };

  const header = (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => navigate("/staff/customers")}
        className="flex w-fit cursor-pointer items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        data-testid="documents-back"
      >
        <ArrowLeft className="h-4 w-4" />
        {"Back to customers"}
      </button>
      <h1 className="mt-2 text-2xl font-bold text-foreground">{"Documents"}</h1>
      {/* Same three facts, same markup and typography as the device-list header
          (CustomerShell → CustomerFacts): text-xs / muted, label in foreground, ids in mono.
          Replicated rather than reusing CustomerFacts because that also renders plan, status and
          screen-count badges — not wanted here. If a fourth fact is ever added, reuse instead. */}
      <div className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
        <span className="block" data-testid="documents-owner-email">
          <span className="text-foreground">{"Owner: "}</span>
          <span>{ownerEmail ?? "(None)"}</span>
          {/* No button without an owner — copying the literal "(None)" is worse than no button. */}
          {ownerEmail && (
            <CopyButton
              value={ownerEmail}
              label="Owner email"
              testId="copy-owner-email"
            />
          )}
        </span>

        {accountId && (
          <span className="block" data-testid="documents-account-id">
            <span className="text-foreground">{"Account id: "}</span>
            <span className="font-mono">{accountId}</span>
            <CopyButton
              value={accountId}
              label="Account id"
              testId="copy-account-id"
            />
          </span>
        )}

        {stripeId && (
          <span className="block" data-testid="documents-stripe-id">
            <span className="text-foreground">{"Stripe id: "}</span>
            <span className="font-mono">{stripeId}</span>
            <CopyButton
              value={stripeId}
              label="Stripe id"
              testId="copy-stripe-id"
            />
          </span>
        )}
      </div>
    </div>
  );

  if (error) {
    return (
      <div
        className="flex flex-col gap-6 p-6"
        data-testid="customer-documents-page"
      >
        {header}
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      </div>
    );
  }

  return (
    // Standalone page now, not a customer-360 tab, so it owns the same container the
    // Customers page uses (flex flex-col gap-6 p-6) plus its own back link + heading.
    <div
      data-testid="customer-documents-page"
      className="flex flex-col gap-6 p-6"
    >
      {header}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, filename, number, state, tags…"
          className="max-w-sm"
          data-testid="document-search-input"
        />

        {/* Type CHIPS, not a folder tree: a document belongs to a customer and a type,
            and a tree would invent a hierarchy the data does not have. */}
        <div className="flex gap-1" data-testid="document-type-chips">
          <Button
            size="sm"
            variant={docType === undefined ? "default" : "outline"}
            onClick={() => setDocType(undefined)}
            data-testid="document-chip-all"
          >
            {"All"}
          </Button>
          {DOC_TYPES.map((t) => (
            <Button
              key={t}
              size="sm"
              variant={docType === t ? "default" : "outline"}
              onClick={() => setDocType(docType === t ? undefined : t)}
              data-testid={`document-chip-${t}`}
            >
              {DOC_TYPE_LABEL[t]}
            </Button>
          ))}
        </div>

        <div className="ml-auto">
          <Button
            onClick={() => setUploadOpen(true)}
            data-testid="document-upload-open"
          >
            {"Upload document"}
          </Button>
        </div>
      </div>

      {fuzzy ? (
        <p
          className="text-xs text-muted-foreground"
          data-testid="document-fuzzy-notice"
        >
          {"No exact matches — showing closest results by name."}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">{"Loading…"}</p>
      ) : documents.length === 0 ? (
        <div
          className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground"
          data-testid="document-empty-state"
        >
          {q || docType
            ? "No documents match those filters."
            : "No documents on file for this customer yet."}
        </div>
      ) : (
        <Table data-testid="document-table">
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">
                {"Upload date"}
              </TableHead>
              <TableHead>{"Title"}</TableHead>
              <TableHead>{"Type"}</TableHead>
              {/*
                Reference number is allowed to wrap to two lines and Invoice ID is not: the
                whole point is fitting a 13" laptop at 100% without a horizontal scrollbar, and
                "Reference number" is the longest header on the row while being almost always
                "—". Wrapping it buys ~65px; keeping "Invoice ID" on one line costs ~15px.
              */}
              <TableHead>{"Reference number"}</TableHead>
              <TableHead className="whitespace-nowrap">
                {"Invoice ID"}
              </TableHead>
              <TableHead className="whitespace-nowrap text-center">
                {"Verification"}
              </TableHead>
              <TableHead className="whitespace-nowrap">{"Actions"}</TableHead>
              <TableHead className="whitespace-nowrap">
                {"Uploaded by"}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatDayCentral(doc.createdAt)}
                </TableCell>
                {/*
                  One line each, ellipsis on overflow. Three details are load-bearing:
                  - the width bound lives on the INNER div, not the cell: an auto-layout table
                    ignores max-width on a td, so constraining the cell does nothing;
                  - min-w-0 on the truncating flex child, or a flex item refuses to shrink
                    below its content and never overflows to trigger the ellipsis;
                  - title attributes, so truncated text stays readable on hover.
                */}
                <TableCell className="font-medium">
                  <div className="flex max-w-[14rem] items-center gap-2">
                    {/* The title is the click target: it is present on every row, whereas
                        the filename line only appears when it differs. */}
                    <button
                      type="button"
                      disabled={busyId === doc.id || doc.status === "archived"}
                      onClick={() => open(doc, "inline")}
                      title={doc.title}
                      className="min-w-0 cursor-pointer truncate text-left underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:no-underline"
                      data-testid={`document-open-${doc.id}`}
                    >
                      {doc.title}
                    </button>
                    {doc.legalHold ? (
                      <Badge
                        variant="destructive"
                        className="shrink-0"
                        title={doc.legalHoldReason ?? ""}
                      >
                        {"Legal hold"}
                      </Badge>
                    ) : null}
                  </div>
                  {filenameAddsInfo(doc.title, doc.currentFilename) ? (
                    // A real <button>, not a clickable div: it is the same action as Preview,
                    // so it must be keyboard-reachable and carry the same disabled rules.
                    // text-left because a button centres its label by default, which would
                    // break alignment with the title above it.
                    <button
                      type="button"
                      disabled={busyId === doc.id || doc.status === "archived"}
                      onClick={() => open(doc, "inline")}
                      title={doc.currentFilename ?? undefined}
                      className="block max-w-[14rem] cursor-pointer truncate text-left text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:hover:text-muted-foreground"
                      data-testid={`document-filename-${doc.id}`}
                    >
                      {doc.currentFilename}
                    </button>
                  ) : null}
                </TableCell>
                <TableCell>{DOC_TYPE_LABEL[doc.docType]}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {doc.externalNumber ?? "—"}
                </TableCell>
                <TableCell>
                  <InvoiceLinkCell doc={doc} prefix="document" onSaved={reload} />
                </TableCell>
                {/*
                  Just the light — matching AgentDeviceStatusDot's h-2 w-2 dot and semantic
                  tokens. No visible label by request, so the state carries entirely in colour;
                  aria-label keeps it available to a screen reader and the title puts the whole
                  story (who verified, when) on hover, since there is no longer any text to read.
                  Sits before Actions so you read the state, then the button that changes it.
                */}
                <TableCell className="whitespace-nowrap text-center">
                  <span
                    aria-label={doc.verifiedAt ? "Verified" : "Not verified"}
                    title={
                      doc.verifiedAt
                        ? `Verified by ${doc.verifiedBy ?? "unknown"} on ${formatDayCentral(doc.verifiedAt)} — can back a tax-exempt discount`
                        : "Not verified — cannot back a tax-exempt discount until verified"
                    }
                    className={`inline-block h-2 w-2 rounded-full ${
                      doc.verifiedAt ? "bg-success" : "bg-destructive"
                    }`}
                    data-testid={`document-verified-dot-${doc.id}`}
                  />
                </TableCell>
                {/*
                  A flex row rather than inline-block + space-x: inline buttons wrap onto a second
                  line as soon as the column narrows, which changes the row height and makes the
                  table jump. shrink-0 keeps the labels intact instead of squeezing the buttons.
                */}
                <TableCell className="whitespace-nowrap">
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      disabled={busyId === doc.id || doc.status === "archived"}
                      onClick={() => void open(doc, "inline")}
                      data-testid={`document-preview-${doc.id}`}
                    >
                      {"Preview"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      disabled={busyId === doc.id || doc.status === "archived"}
                      onClick={() => void open(doc, "attachment")}
                      data-testid={`document-download-${doc.id}`}
                    >
                      {"Download"}
                    </Button>
                    {/*
                      ROOT only, hidden for support. Two-step by design: on a live row this
                      archives (reversible); on an already-archived row it erases the bytes for
                      good. The dialog copy differs accordingly, because the two actions are not
                      remotely equally serious.
                    */}
                    {isRoot ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="shrink-0"
                            disabled={busyId === doc.id || doc.legalHold}
                            aria-label={
                              doc.status === "archived"
                                ? "Delete permanently"
                                : "Archive document"
                            }
                            title={
                              doc.legalHold
                                ? "Under legal hold — cannot be archived or deleted"
                                : doc.status === "archived"
                                  ? "Delete permanently — erases the file from storage"
                                  : "Archive (reversible). Delete again to erase permanently."
                            }
                            data-testid={`document-delete-${doc.id}`}
                          >
                            <Trash2
                              className={
                                doc.status === "archived"
                                  ? "h-4 w-4 text-destructive"
                                  : "h-4 w-4 text-muted-foreground"
                              }
                            />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent
                          data-testid={`document-delete-confirm-${doc.id}`}
                        >
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {doc.status === "archived"
                                ? "Delete this document permanently?"
                                : "Archive this document?"}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {doc.status === "archived"
                                ? `"${doc.title}" and every stored version of the file will be erased from Cloudflare storage. This cannot be undone. The access log entry recording the deletion is kept.`
                                : `"${doc.title}" will be hidden from this list and refused as tax-exemption evidence. The file is kept and you can restore it. To erase the file itself, delete it again once archived.`}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel
                              data-testid={`document-delete-cancel-${doc.id}`}
                            >
                              {"Cancel"}
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                void (doc.status === "archived"
                                  ? doPurge(doc)
                                  : doArchive(doc))
                              }
                              data-testid={`document-delete-go-${doc.id}`}
                            >
                              {doc.status === "archived"
                                ? "Delete permanently"
                                : "Archive"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : null}
                  </div>
                </TableCell>
                {/*
                  Truncated, not wrapped: every address here is @optisigns.com, so the tail is
                  the least informative part of the row and was already being clipped by the
                  horizontal scroll. Full value on hover.
                */}
                <TableCell className="text-muted-foreground">
                  <span
                    className="block max-w-[9rem] truncate"
                    title={doc.createdBy}
                  >
                    {doc.createdBy}
                  </span>
                </TableCell>
                {/*
                  Its own column, not just the button label: Apply Coupon refuses an unverified
                  tax-exemption certificate, so "which of these can back a discount" has to be
                  answerable by scanning the table rather than by hovering each row.
                */}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {documents.length > 0 ? (
        <p
          className="text-xs text-muted-foreground"
          data-testid="document-total"
        >
          {`${total} document${total === 1 ? "" : "s"}`}
        </p>
      ) : null}

      {accountId ? (
        <DocumentUploadDialog
          accountId={accountId}
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          onUploaded={reload}
        />
      ) : null}
    </div>
  );
}
